import { useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import {
  IBusinessWeekNode,
  IListBusinessWeeksResponse,
  IListBusinessWeeksVariables,
  IListLocationMetricValuesV2Response,
  IListLocationMetricValuesV2Variables,
  ILocationMetricNode,
  LIST_BUSINESS_WEEKS,
  LIST_LOCATION_METRIC_VALUES_V2,
} from '../gql'
import { ICompDateDataType } from '../hooks/useGetDateInfo'

export interface IAccountingSalesReportData extends ILocationMetricNode {
  week: number
  weekendDate: string
  formattedWeekendDate: string
  month: string
  isLastWeekOfMonth: boolean
  monthlySales: number // Sum of total_sales for the month
  actualSales: number // total_sales
  percentLW: number // % change from previous week
  monthlySalesPastYear: number // Sum of net_sales_sss for the month
  actualSalesPastYear: number // net_sales_sss
  compPercentLY: number // net_sales_sss_percent
  budgetedSales: number // total_sales_budget
  cumulativeSales: number // Cumulative sum of total_sales
  cumulativeSalesPastYear: number // Cumulative sum of net_sales_sss
  ytdCompLYMonthly: number // (Monthly Sales 2025 - Monthly Sales 2024) / Monthly Sales 2024
  ytdCompLYCumulative: number // (Cumulative Sales 2025 - Cumulative Sales 2024) / Cumulative Sales 2024
  showPastYearData: boolean
}

interface IReturnType {
  dataSource: IAccountingSalesReportData[]
  loading: boolean
}

interface IUseAccountingSalesReportDatasourceProps {
  locationId: number
  startDate?: string
  endDate?: string
  selectedYear?: number
  compDateData?: ICompDateDataType | null
}

// Helper function to convert units
const convertUnit = (value: number, unit?: string): number => {
  if (!value && value !== 0) return 0

  if (unit === 'CENT') {
    return value / 100
  }
  if (unit === 'PERCENTAGE') {
    return value * 100
  }
  return value
}

export default function useAccountingSalesReportDatasource({
  locationId,
  startDate,
  endDate,
  selectedYear,
  compDateData,
}: IUseAccountingSalesReportDatasourceProps): IReturnType {
  // Get the year from the dates
  const currentYear = selectedYear || new Date().getFullYear()
  const previousYear = selectedYear
    ? selectedYear - 1
    : new Date().getFullYear() - 1

  // Fetch business weeks data
  const { data: businessWeeksData, loading: businessWeeksLoading } = useQuery<
    IListBusinessWeeksResponse,
    IListBusinessWeeksVariables
  >(LIST_BUSINESS_WEEKS, {
    variables: {
      minYear: previousYear - 1,
      maxYear: currentYear + 1,
    },
    skip: !selectedYear,
  })

  // Current year data
  const { data, loading: locationMetricValuesLoading } = useQuery<
    IListLocationMetricValuesV2Response,
    IListLocationMetricValuesV2Variables
  >(LIST_LOCATION_METRIC_VALUES_V2, {
    variables: {
      iStartDate: startDate || '',
      iEndDate: endDate || '',
      iParams: {
        group_by: ['LOCATION', 'BUSINESS_WEEK'],
        location_ids: [locationId],
        metrics: ['total_sales', 'total_sales_budget'],
      },
    },
    skip: !startDate || !endDate || !locationId,
  })

  // Calculate previous year date range using business weeks data
  const { pyStartDate, pyEndDate } = useMemo(() => {
    if (!businessWeeksData?.listBusinessWeeks?.nodes || !startDate) {
      return { pyStartDate: '', pyEndDate: '' }
    }

    // Find the first week of the previous year
    const firstWeekOfPreviousYear =
      businessWeeksData.listBusinessWeeks.nodes.find(
        (week) => week.businessYear === previousYear && week.businessWeek === 1,
      )

    const pyStartDate = firstWeekOfPreviousYear?.businessWeekBeginDate || ''

    // pyEndDate is current year's startDate minus 1 day
    const pyEndDate = startDate
      ? moment(startDate).subtract(1, 'day').format('YYYY-MM-DD')
      : ''

    return { pyStartDate, pyEndDate }
  }, [businessWeeksData, startDate])

  // Previous year data
  const { data: previousYearData, loading: previousYearLoading } = useQuery<
    IListLocationMetricValuesV2Response,
    IListLocationMetricValuesV2Variables
  >(LIST_LOCATION_METRIC_VALUES_V2, {
    variables: {
      iStartDate: pyStartDate,
      iEndDate: pyEndDate,
      iParams: {
        group_by: ['LOCATION', 'BUSINESS_WEEK'],
        location_ids: [locationId],
        metrics: ['total_sales'],
      },
    },
    skip: !pyStartDate || !pyEndDate || !locationId,
  })

  return {
    dataSource: useMemo(() => {
      if (!data?.listLocationMetricValuesV2?.nodes) return []
      if (!previousYearData?.listLocationMetricValuesV2?.nodes) return []
      if (!businessWeeksData?.listBusinessWeeks?.nodes) return []

      // Create a map of business weeks for quick lookup
      const businessWeeksMap = new Map<string, IBusinessWeekNode>()
      businessWeeksData.listBusinessWeeks.nodes.forEach((week) => {
        businessWeeksMap.set(week.businessWeekEndDate, week)
      })

      // Create a map of previous year data by business week
      const previousYearMap = new Map<number, number>()
      if (previousYearData?.listLocationMetricValuesV2?.nodes) {
        previousYearData.listLocationMetricValuesV2.nodes.forEach((node) => {
          const businessWeek = businessWeeksMap.get(node.endDate)
          if (businessWeek) {
            const totalSalesValue = convertUnit(
              node.metricData.total_sales?.value,
              node.metricData.total_sales?.unit,
            )
            previousYearMap.set(businessWeek.businessWeek, totalSalesValue)
          }
        })
      }

      const processedData = data.listLocationMetricValuesV2.nodes.map(
        (node, index) => {
          const weekNumber = index + 1
          const endDate = moment(node.endDate)
          const businessMonth =
            businessWeeksMap.get(node.endDate)?.businessMonth || 1
          const monthName = businessMonth
            ? moment({ month: businessMonth - 1 }).format('MMMM')
            : endDate.format('MMMM')
          const formattedWeekendDate = endDate.format('M/D/YY')

          // Get business week info for matching with previous year
          const businessWeek = businessWeeksMap.get(node.endDate)
          const businessWeekNumber = businessWeek?.businessWeek || 0

          // Get previous year sales from the map
          const previousYearSales = previousYearMap.get(businessWeekNumber) || 0

          // Calculate comp percent
          const currentSales = convertUnit(
            node.metricData.total_sales?.value,
            node.metricData.total_sales?.unit,
          )
          const compPercentLY =
            previousYearSales !== 0
              ? ((currentSales - previousYearSales) / previousYearSales) * 100
              : 0

          // Check if the week is after current week
          const isAfterCurrentWeek = endDate.isAfter(
            moment().add(1, 'week'),
            'week',
          )

          return {
            ...node,
            week: weekNumber,
            weekendDate: node.endDate,
            formattedWeekendDate,
            month: monthName,
            isLastWeekOfMonth: false, // Will be determined later
            monthlySales: 0, // Will be calculated later
            actualSales: currentSales,
            percentLW: 0, // Will be calculated later
            monthlySalesPastYear: 0, // Will be calculated later
            actualSalesPastYear: previousYearSales,
            compPercentLY,
            budgetedSales: isAfterCurrentWeek
              ? 0 // Will be displayed as "-" in the column
              : convertUnit(
                  node.metricData.total_sales_budget?.value,
                  node.metricData.total_sales_budget?.unit,
                ),
            cumulativeSales: 0, // Will be calculated later
            cumulativeSalesPastYear: 0, // Will be calculated later
            ytdCompLYMonthly: 0, // Will be calculated later
            ytdCompLYCumulative: 0, // Will be calculated later
          }
        },
      )

      // Calculate % LW (week-over-week)
      const dataWithPercentLW = processedData.map((row, index) => {
        const prevRow = processedData[index - 1]
        let percentLW = 0
        if (prevRow && prevRow.actualSales !== 0) {
          percentLW =
            ((row.actualSales - prevRow.actualSales) / prevRow.actualSales) *
            100
        }
        return { ...row, percentLW }
      })

      // Determine last week of each business month and calculate monthly sums
      const dataWithMonthlyCalcs = dataWithPercentLW.map((row, index) => {
        // Get business week info for this row
        const businessWeek = businessWeeksMap.get(row.endDate)
        const currentBusinessMonth = businessWeek?.businessMonth || 0

        // Check if this is the last week of the business month
        const nextRow = dataWithPercentLW[index + 1]
        let isLastWeekOfMonth = false

        if (nextRow) {
          const nextBusinessWeek = businessWeeksMap.get(nextRow.endDate)
          isLastWeekOfMonth =
            nextBusinessWeek?.businessMonth !== currentBusinessMonth
        } else {
          // If there's no next row, this is the last week
          isLastWeekOfMonth = true
        }

        // Calculate monthly sums for this business month
        let monthlySales = 0
        let monthlySalesPastYear = 0

        if (isLastWeekOfMonth) {
          // Sum all weeks in this business month
          for (let i = index; i >= 0; i--) {
            const checkRow = dataWithPercentLW[i]
            const checkBusinessWeek = businessWeeksMap.get(checkRow.endDate)
            if (checkBusinessWeek?.businessMonth === currentBusinessMonth) {
              monthlySales += checkRow.actualSales
              monthlySalesPastYear += checkRow.actualSalesPastYear
            } else {
              break
            }
          }
        }

        // Calculate YTD Comp % LY for monthly
        let ytdCompLYMonthly = 0
        if (monthlySalesPastYear !== 0) {
          ytdCompLYMonthly =
            ((monthlySales - monthlySalesPastYear) / monthlySalesPastYear) * 100
        }

        return {
          ...row,
          isLastWeekOfMonth,
          monthlySales,
          monthlySalesPastYear,
          ytdCompLYMonthly,
        }
      })

      // Calculate cumulative values and YTD Comp % LY Cumulative
      let cumulativeCy = 0
      let cumulativePy = 0

      return dataWithMonthlyCalcs.map((row) => {
        cumulativeCy += row.actualSales
        cumulativePy += row.actualSalesPastYear

        let ytdCompLYCumulative = 0
        if (cumulativePy !== 0) {
          ytdCompLYCumulative =
            ((cumulativeCy - cumulativePy) / cumulativePy) * 100
        }

        // Determine if past year data should be shown based on compDateData
        let showPastYearData = true // Default to showing past year data

        if (compDateData) {
          if (compDateData.status === 'new') {
            // New location: hide all past year data
            showPastYearData = false
          } else if (compDateData.status === 'comp') {
            // Comp location: show all past year data
            showPastYearData = true
          } else if (compDateData.status === 'become-comp') {
            // Became comp: show past year data only for weeks >= comp week
            showPastYearData = row.week >= compDateData.weekNumber
          }
        }

        return {
          ...row,
          cumulativeSales: cumulativeCy,
          cumulativeSalesPastYear: cumulativePy,
          ytdCompLYCumulative,
          showPastYearData,
        }
      })
    }, [data, businessWeeksData, compDateData, previousYearData]),
    loading:
      locationMetricValuesLoading ||
      businessWeeksLoading ||
      previousYearLoading,
  }
}
